import React, { useState, useEffect, useRef } from 'react';

// The Toast component with a progress bar
const Toast = ({ message, type, onClose, duration }) => {
    // Refs to manage the animation loop and timing
    const animationFrameRef = useRef(null);
    const startTimeRef = useRef(null);
    const progressBarRef = useRef(null); // Ref for the progress bar element

    // Use a state variable to control the width
    const [progressWidth, setProgressWidth] = useState(100);

    // useEffect to manage the toast's animation and closure
    useEffect(() => {
        // Function to animate the progress bar
        const animateProgressBar = (timestamp) => {
            if (!startTimeRef.current) {
                startTimeRef.current = timestamp;
            }
            const elapsedTime = timestamp - startTimeRef.current;
            const remainingTime = Math.max(0, duration - elapsedTime);
            const newProgress = (remainingTime / duration) * 100;

            // Update the progress state to re-render the bar
            setProgressWidth(newProgress);

            // Continue the animation if time remains
            if (remainingTime > 0) {
                animationFrameRef.current = requestAnimationFrame(animateProgressBar);
            }
        };

        // Start the animation loop
        animationFrameRef.current = requestAnimationFrame(animateProgressBar);

        // Set a timeout to automatically close the toast after the duration
        const timer = setTimeout(() => {
            if (typeof onClose === 'function') {
                onClose();
            }
        }, duration);

        // Cleanup function to clear the timer and animation frame on unmount
        return () => {
            clearTimeout(timer);
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [duration, onClose]);

    // This useEffect will run when `progressWidth` changes, updating the DOM
    useEffect(() => {
        if (progressBarRef.current) {
            progressBarRef.current.style.width = `${progressWidth}%`;
        }
    }, [progressWidth]);

    let bgColorClass = '';
    let textColorClass = '';
    let progressColorClass = '';

    switch (type) {
        case 'success':
            bgColorClass = 'bg-green-100 dark:bg-green-900';
            textColorClass = 'text-green-800 dark:text-green-200';
            progressColorClass = 'bg-green-500';
            break;
        case 'error':
            bgColorClass = 'bg-red-100 dark:bg-red-900';
            textColorClass = 'text-red-800 dark:text-red-200';
            progressColorClass = 'bg-red-500';
            break;
        case 'warning':
            bgColorClass = 'bg-yellow-100 dark:bg-yellow-900';
            textColorClass = 'text-yellow-800 dark:text-yellow-200';
            progressColorClass = 'bg-yellow-500';
            break;
        default: // info or default
            bgColorClass = 'bg-gray-100 dark:bg-gray-700';
            textColorClass = 'text-gray-800 dark:text-gray-200';
            progressColorClass = 'bg-gray-500';
    }

    return (
        <div className={`fixed bottom-8 left-1/2 -translate-x-1/2 flex flex-col p-4 rounded-lg shadow-lg text-base ${bgColorClass} ${textColorClass} transition-opacity duration-300 ease-in-out z-50`}>
            <div className="flex items-center justify-between w-full">
                <span className="mr-2 text-xl">
                    {type === 'success' && '✔'}
                    {type === 'error' && '✖'}
                    {type === 'warning' && '⚠'}
                    {type === 'info' && 'ℹ'}
                </span>
                <span className="flex-grow">{message}</span>
                <button className={`ml-4 text-xl bg-transparent border-none ${textColorClass} cursor-pointer`} onClick={() => typeof onClose === 'function' && onClose()}>✕</button>
            </div>
            <div className="w-full h-1 bg-gray-300 dark:bg-gray-600 rounded-full mt-2 overflow-hidden">
                <div ref={progressBarRef} className={`${progressColorClass} h-full`} style={{ width: `${progressWidth}%` }}></div>
            </div>
        </div>
    );
};

export default Toast;